import * as DialogPrimitive from "@radix-ui/react-dialog";
import { usePostHog } from "@posthog/react";
import { Check, X } from "lucide-react";
import { useEffect } from "react";

import {
  PLUS_PRICE,
  UPGRADE_COPY,
  type UpgradeReason,
} from "@sill/shared/components/billing/pricing-copy";

import { captureUpgradeModalEvent } from "../../lib/analytics";

export interface UpgradeModalProps {
  // The action being gated. Drives the contextual headline + the LEAD bullet
  // from UPGRADE_COPY[feature].bullets so the value-exchange lands first.
  feature: UpgradeReason;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Caller-provided so the modal can stay agnostic about Clerk Billing
  // checkout vs route navigation — every Plus-gated callsite already knows
  // how it wants to convert the click.
  onUpgradeClick: () => void;
  onComparePlansClick: () => void;
}

// UpgradeModal is the **action-interception** upsell: a single Plus card,
// three lead-with-feature bullets, one primary upgrade CTA, and a compare
// link. Distinct from `UpgradeSheet` (full pricing comparison) — this modal
// surfaces the value-exchange the moment the user clicks a Plus-gated
// affordance, and dismisses cheaply. No plan toggle, no Clerk PricingTable.
export function UpgradeModal({
  feature,
  open,
  onOpenChange,
  onUpgradeClick,
  onComparePlansClick,
}: UpgradeModalProps) {
  const copy = UPGRADE_COPY[feature];
  const posthog = usePostHog();
  const captureFn = posthog?.capture.bind(posthog);

  // Effect runs before the early-return so feature changes inside an open
  // modal also re-fire the impression — every distinct (feature, open=true)
  // is its own conversion-funnel entry.
  useEffect(() => {
    if (open && captureFn) {
      captureUpgradeModalEvent(captureFn, "Opened", feature);
    }
  }, [open, feature, captureFn]);

  if (!open) return null;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          if (captureFn) {
            captureUpgradeModalEvent(captureFn, "Dismissed", feature);
          }
          onOpenChange(false);
        } else {
          onOpenChange(true);
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm" />
        <DialogPrimitive.Content
          data-testid="upgrade-modal"
          aria-describedby="upgrade-modal-subtitle"
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-background p-6 shadow-xl focus:outline-none"
        >
          <DialogPrimitive.Close
            data-slot="interactive-control"
            data-testid="upgrade-modal-close"
            aria-label="Close upgrade modal"
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>

          <div className="space-y-1 pr-8">
            <DialogPrimitive.Title className="text-xl font-semibold tracking-tight text-foreground">
              {copy.title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description
              id="upgrade-modal-subtitle"
              className="text-sm text-muted-foreground"
            >
              {copy.subtitle}
            </DialogPrimitive.Description>
          </div>

          <div className="mt-6 rounded-xl bg-card p-5">
            <div className="flex items-baseline justify-between">
              <p className="text-base font-semibold text-foreground">Plus</p>
              <p
                data-testid="upgrade-modal-price"
                className="text-base font-semibold text-foreground"
              >
                ${PLUS_PRICE.monthly.amount}
                <span className="text-sm font-normal text-muted-foreground">
                  {PLUS_PRICE.monthly.periodLabel}
                </span>
              </p>
            </div>
            <ul className="mt-4 space-y-2">
              {copy.bullets.map((bullet, i) => (
                <li
                  key={bullet}
                  data-testid="upgrade-modal-bullet"
                  className="flex items-start gap-2 text-sm text-foreground"
                >
                  <Check
                    className={`mt-0.5 h-4 w-4 shrink-0 ${
                      i === 0 ? "text-primary" : "text-muted-foreground"
                    }`}
                    aria-hidden="true"
                  />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 space-y-2">
            <button
              type="button"
              data-slot="interactive-control"
              data-testid="upgrade-modal-upgrade"
              onClick={() => {
                if (captureFn) {
                  captureUpgradeModalEvent(captureFn, "UpgradeClicked", feature);
                }
                onUpgradeClick();
              }}
              className="w-full rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              Upgrade to Plus
            </button>
            <button
              type="button"
              data-testid="upgrade-modal-compare"
              onClick={() => {
                if (captureFn) {
                  captureUpgradeModalEvent(
                    captureFn,
                    "ComparePlansClicked",
                    feature,
                  );
                }
                onComparePlansClick();
              }}
              className="w-full text-sm font-medium text-primary underline-offset-4 hover:underline focus:outline-none focus-visible:underline"
            >
              Compare all plans →
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
